import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ICONS from "../../../assets/Icons";
import {
  favouriteNotification,
  notificationsBulkEdit,
  notificationsViewAll,
  snoozeNotification,
  updateNotificationArrayInStore,
  updateNotificationCount,
  updateNotificationInStore,
} from "../../../redux/features/Notifications";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../utils/configurationProvider";
import useDynamicRouting from "../../../utils/useDynamicRouting";
import PrimaryButton from "../../_utils/CommonButtons/PrimaryButton";
import IconRoundButton from "../../_utils/CommonButtons/IconRoundButton";
import Loader from "../../Loader/Index";
import SnoozePopup from "../../_utils/SnoozePopup";
import SVGComponent from "../../_utils/SVGComponent";
import NotificationPopupCard from "../NotificationPopupCard";

// -------------------- Helper Functions --------------------

// Returns the notification ID from either the `id` or `notification_id` field.
// Supports both ID formats that may be returned by the notification API.
const getNotificationId = (notification) =>
  notification?.id || notification?.notification_id;

// Determines whether a notification has already been marked as read.
const isNotificationRead = (notification) =>
  notification?.read === true || notification?.read === "true";

// Extracts the notification array from the API response.
const getNotificationList = (notificationsData) => {
  if (Array.isArray(notificationsData?.notifications)) {
    return notificationsData.notifications;
  }

  if (Array.isArray(notificationsData?.data)) {
    return notificationsData.data;
  }

  return [];
};

const NotificationPopup = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Provides dynamic routing for linked notification items.
  const { handleDynamicRouting } = useDynamicRouting();

  // Retrieves the services assigned to the current user.
  const services = useSelector((state) => state.refreshToken.services);

  // Retrieves notification data maintained in the Redux notification slice.
  const { notificationsData } = useSelector(
    (state) => state?.notificationsSlice,
  );

  // Controls the popup close animation.
  const [animate, setAnimate] = useState(false);

  // Stores the ID of the notification currently being animated,
  // such as when a notification is snoozed.
  const [animatingNotificationId, setAnimatingNotificationId] = useState(null);

  // Controls the loading state while notification data is being fetched.
  const [isLoading, setIsLoading] = useState(false);

  // Controls whether the snooze popup is displayed.
  const [showSnoozePopup, setShowSnoozePopup] = useState(false);

  // Stores the notification selected for the snooze action.
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Stores the complete active notification list returned by the API.
  // This is used to determine whether any notifications exist and
  // whether the "View More" / "View all" button should be enabled.
  const [allNotificationsData, setAllNotificationsData] = useState([]);

  // This state stores the summary of all notifications, including counts of unread and total notifications.
  // It is used to display the notification summary in the popup.
  const [allNotificationsSummary, setAllNotificationsSummary] = useState({});

  // Keeps the unread count stable when notifications are automatically marked as read.
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const hasAnyNotifications = allNotificationsData.length > 0;

  const [localReadVersion, setLocalReadVersion] = useState(0);

  // Tracks whether at least one unread notification exists.
  // This determines whether the footer button displays "View More" or "View all".
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);

  // Reference to the scrollable notification container.
  // Used as the root element for IntersectionObserver.
  const scrollContainerRef = useRef(null);

  // Stores active visibility timers for individual notifications.
  // The notification ID is used as the map key.
  const visibilityTimersRef = useRef(new Map());

  // Stores notification IDs that have already been processed as visible.
  // Prevents the same notification from repeatedly triggering
  // the mark-as-read action.
  const markedVisibleNotificationIdsRef = useRef(new Set());

  const pendingReadNotificationIdsRef = useRef(
    new Set(
      JSON.parse(
        sessionStorage.getItem("pending_notification_read_ids") || "[]",
      ),
    ),
  );
  const locallyReadNotificationIdsRef = useRef(new Set());

  const hasFlushedReadNotificationsRef = useRef(false);

  // Tracks notification IDs so only newly inserted websocket notifications affect the count.
  const knownNotificationIdsRef = useRef(null);
  const hasFetchedNotificationsRef = useRef(false);

  // Determines whether the service information is still loading.
  const isServicesLoading = services === undefined || services === null;

  // Determines whether the user has direct access to the Notify service.
  const canUseNotifyService = services?.includes("Notify");

  // Determines whether the user can view notifications.
  const notifyServiceAvailable =
    services?.includes("Notify") || services?.includes("Admin");

  // Creates the notification list displayed inside the popup.
  const notificationList = useMemo(() => {
    const nowTs = Math.floor(Date.now() / IntervalConfiguration.intervalMedium);

    return getNotificationList(notificationsData)
      .filter(
        (item) => !(item?.snoozed_until && Number(item?.snoozed_until) > nowTs),
      )
      .map((item) => {
        const notificationId = getNotificationId(item);

        if (
          notificationId &&
          locallyReadNotificationIdsRef.current.has(String(notificationId))
        ) {
          return {
            ...item,
            read: true,
          };
        }

        return item;
      });
  }, [notificationsData, localReadVersion]);

  // Limits the number of notifications rendered in the popup to 50.
  const renderedNotificationList = useMemo(
    () => notificationList.slice(0, 50),
    [notificationList],
  );

  // Controls whether the "Showing the latest 50 unread notifications."
  // helper text should be displayed.
  const shouldShowLatestFiftyText = !isLoading && notificationList.length > 0;

  const flushPendingReadNotifications = useCallback(async () => {
    if (hasFlushedReadNotificationsRef.current) {
      return;
    }

    const notificationIds = Array.from(pendingReadNotificationIdsRef.current);

    if (!notificationIds.length || !canUseNotifyService) {
      return;
    }

    hasFlushedReadNotificationsRef.current = true;

    try {
      const response = await dispatch(
        notificationsBulkEdit({
          read: true,
          notification_ids: notificationIds,
        }),
      );

      if (
        response?.meta?.requestStatus === "fulfilled" &&
        response?.payload?.status !== false
      ) {
        pendingReadNotificationIdsRef.current.clear();

        sessionStorage.removeItem("pending_notification_read_ids");
        dispatch(
          updateNotificationCount({
            summary: {
              unread: Math.max(
                0,
                Number(notificationsData?.summary?.unread || 0) -
                  notificationIds.length,
              ),
            },
          }),
        );
      } else {
        hasFlushedReadNotificationsRef.current = false;
      }
    } catch (error) {
      hasFlushedReadNotificationsRef.current = false;

      console.error("Failed to bulk mark notifications as read", error);
    }
  }, [canUseNotifyService, dispatch, notificationsData?.summary?.unread]);

  /**
   * Closes the notification popup using the configured
   * popup closing animation duration.
   */
  const closePopup = useCallback(async () => {
    await flushPendingReadNotifications();

    setAnimate(true);

    setTimeout(() => {
      onClose?.();
    }, IntervalConfiguration.intervalSemiMid);
  }, [flushPendingReadNotifications, onClose]);

  /**
   * Marks a notification as read.
   * This function is triggered after a notification has remained
   * sufficiently visible inside the popup for the configured duration.
   */
  const queueNotificationForRead = useCallback(
    (notification) => {
      const id = getNotificationId(notification);

      if (!id || !canUseNotifyService) {
        return;
      }

      const idKey = String(id);

      pendingReadNotificationIdsRef.current.add(idKey);
      locallyReadNotificationIdsRef.current.add(idKey);

      sessionStorage.setItem(
        "pending_notification_read_ids",
        JSON.stringify(Array.from(pendingReadNotificationIdsRef.current)),
      );

      setLocalReadVersion((version) => version + 1);
    },
    [canUseNotifyService],
  );

  /**
   * Snoozes the selected notification until the specified date/time.
   * After a successful API response, the notification is animated
   * and the Redux notification list is updated so that another
   * notification can take its place in the popup.
   */
  const handleSnoozeConfirm = useCallback(
    async (id, snoozed_until) => {
      if (!id) return;

      try {
        const response = await dispatch(
          snoozeNotification({
            id,
            snoozed_until,
          }),
        );

        if (response?.payload?.status !== true) {
          return;
        }

        setAnimatingNotificationId(String(id));

        setTimeout(() => {
          dispatch(
            updateNotificationInStore({
              response: response.payload,
              stateToUpdate: "notificationsData",
            }),
          );

          dispatch(
            updateNotificationArrayInStore({
              response: response.payload,
              stateToUpdate: "notificationsData",
              notificationLength:
                notificationList.length || GlobalConfiguration.lengthCheckZero,
            }),
          );

          setAnimatingNotificationId(null);
        }, 600);
      } catch (error) {
        console.error("Failed to snooze notification", error);
      } finally {
        setShowSnoozePopup(false);
        setSelectedNotification(null);
      }
    },
    [dispatch, notificationList.length],
  );

  /**
   * Toggles the favourite state of a notification.
   * The current favourite state is inverted and sent to the API.
   * On success, the updated notification is stored in Redux.
   */
  const handleFavouriteNotification = useCallback(
    async (notification) => {
      const id = getNotificationId(notification);

      if (!id || !canUseNotifyService) {
        return;
      }

      try {
        const response = await dispatch(
          favouriteNotification({
            id,
            favourite: !notification?.favourite,
          }),
        );

        if (!response?.payload?.status) return;

        dispatch(
          updateNotificationInStore({
            response: response.payload,
            stateToUpdate: "notificationsData",
          }),
        );
      } catch (error) {
        console.error("Failed to toggle favourite notification", error);
      }
    },
    [canUseNotifyService, dispatch],
  );

  /**
   * Navigates to the Notification Dashboard.
   *
   * Dashboard access is available to users with Admin, Notify,
   * CChannel, or VChannel service access.
   */
  const handleDashboardClick = () => {
    if (
      services?.includes("Admin") ||
      services?.includes("Notify") ||
      services?.includes("CChannel") ||
      services?.includes("VChannel")
    ) {
      navigate("/home/notification_dashboard");
      onClose?.();
      return;
    }

    navigate("/feature_not_assigned", {
      state: {
        unavailableService: "Admin, Notify, CChannel, VChannel",
      },
    });
  };

  /**
   * Navigates to the View All Notifications page.
   *
   * When unread notifications exist, the page opens normally.
   * When there are no unread notifications but active notifications exist,
   * the `read: "all"` state is passed so the destination can display
   * all notifications.
   */
  const handleViewMoreClick = () => {
    if (!allNotificationsData.length) return;

    if (services?.includes("Admin") || services?.includes("Notify")) {
      if (!isLoading && notificationList.length > 0) {
        navigate("/home/notification_dashboard/view_notifications");
      } else {
        navigate("/home/notification_dashboard/view_notifications", {
          state: { read: "all" },
        });
      }

      onClose?.();
      return;
    }

    navigate("/feature_not_assigned", {
      state: {
        unavailableService: "Admin, Notify",
      },
    });
  };

  /**
   * Opens the linked item associated with a notification.
   *
   * The dynamic routing utility determines the destination based
   * on the notification's eventable type and ID.
   */
  const handleViewLinkedItem = (notification) => {
    handleDynamicRouting(
      notification?.eventable_type,
      notification?.eventable_id || notification?.eventable_ids,
      notification?.title,
    );
    onClose?.();
  };

  /**
   * Opens the detailed notification page for a selected notification.
   * The complete notification object is passed through router state
   * so the detail page can immediately access the selected notification.
   */
  const handleViewNotification = (notificationId, notification) => {
    if (!notificationId) return;

    navigate(
      `/home/notification_dashboard/notification_detail_page/${notificationId}`,
      {
        state: { notification },
      },
    );
    onClose?.();
  };

  /**
   * Fetches both active notifications and active unread notifications
   * when the popup is mounted.
   *
   * Two requests are used because:
   * - `allNotificationsData` determines whether any active notifications exist.
   * - `hasUnreadNotifications` determines whether the footer button
   *   should display "View More" or "View all".
   */
  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);

      try {
        const allResponse = await dispatch(
          notificationsViewAll({
            active: true,
          }),
        );

        const allNotificationsSummary = allResponse?.payload?.summary || {};
        setAllNotificationsSummary(allNotificationsSummary);

        const allNotifications =
          allResponse?.payload?.notifications ||
          allResponse?.payload?.data ||
          [];

        setAllNotificationsData(allNotifications);

        const unreadResponse = await dispatch(
          notificationsViewAll({
            active: true,
            page_size: 100,
            read: false,
          }),
        );

        const unreadNotifications =
          unreadResponse?.payload?.notifications ||
          unreadResponse?.payload?.data ||
          [];

        setHasUnreadNotifications(unreadNotifications.length > 0);
      } finally {
        hasFetchedNotificationsRef.current = true;
        setIsLoading(false);
      }
    };

    fetchNotifications();
  }, [dispatch]);

  useEffect(() => {
    if (isLoading || !hasFetchedNotificationsRef.current) return;

    const currentNotificationIds = new Set(
      notificationList.map((notification) =>
        String(getNotificationId(notification)),
      ),
    );

    if (knownNotificationIdsRef.current === null) {
      knownNotificationIdsRef.current = currentNotificationIds;
      setUnreadNotificationCount(
        notificationList.filter(
          (notification) => !isNotificationRead(notification),
        ).length,
      );
      return;
    }

    const newUnreadNotificationCount = notificationList.filter(
      (notification) =>
        !knownNotificationIdsRef.current.has(
          String(getNotificationId(notification)),
        ) && !isNotificationRead(notification),
    ).length;

    if (newUnreadNotificationCount > 0) {
      setUnreadNotificationCount((count) => count + newUnreadNotificationCount);
    }

    knownNotificationIdsRef.current = currentNotificationIds;
  }, [isLoading, notificationList]);

  // Resets the notification list scroll position to the top
  // whenever the popup is initially mounted.
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({
      top: GlobalConfiguration.lengthCheckZero,
      behavior: "auto",
    });
  }, []);

  useEffect(() => {
    return () => {
      flushPendingReadNotifications();
    };
  }, [flushPendingReadNotifications]);

  /**
   * Observes the visibility of notifications inside the popup.
   *
   * A notification must:
   * - Be at least 90% visible inside the popup scroll container.
   * - Remain visible at that threshold for 2 seconds.
   *
   * Once both conditions are satisfied, the notification is marked as read.
   *
   * IntersectionObserver is used instead of listening directly to scroll
   * events so that visibility can be tracked efficiently.
   */
  useEffect(() => {
    // Do not create the observer while notification data is loading.
    if (isLoading) return undefined;

    const container = scrollContainerRef.current;

    // Exit if the scroll container is not available.
    if (!container) return undefined;

    const visibilityTimers = visibilityTimersRef.current;

    // A notification is considered sufficiently visible
    // when at least 90% of it is inside the container.
    const fullyVisibleThreshold = 0.9;

    /**
     * Clears the visibility timer for a notification.
     *
     * This is called when the notification becomes less than
     * the required visibility threshold.
     */
    const clearVisibilityTimer = (notificationIdKey) => {
      const timer = visibilityTimers.get(notificationIdKey);

      if (timer) {
        clearTimeout(timer);
        visibilityTimers.delete(notificationIdKey);
      }
    };

    /**
     * Starts the visibility timer for a notification.
     *
     * The timer is only created when the notification:
     * - Has not already been processed.
     * - Does not already have an active timer.
     * - Exists in the current notification list.
     * - Is not already marked as read.
     */
    const startVisibilityTimer = (notificationId) => {
      if (!notificationId) return;

      const notificationIdKey = String(notificationId);

      // Prevent duplicate timers and repeated mark-as-read processing.
      if (
        markedVisibleNotificationIdsRef.current.has(notificationIdKey) ||
        visibilityTimers.has(notificationIdKey)
      ) {
        return;
      }

      // Find the notification associated with the observed DOM element.
      const notification = notificationList.find(
        (item) => String(getNotificationId(item)) === notificationIdKey,
      );

      if (!notification) return;

      // Already-read notifications do not need a visibility timer.
      if (isNotificationRead(notification)) {
        markedVisibleNotificationIdsRef.current.add(notificationIdKey);
        return;
      }

      // Wait 2 seconds before marking the notification as read.
      const timer = setTimeout(() => {
        // Mark as processed so this notification does not
        // start another visibility timer.
        markedVisibleNotificationIdsRef.current.add(notificationIdKey);

        visibilityTimers.delete(notificationIdKey);

        // Store the ID for the bulk API request.
        // The UI is updated immediately; no API call is made here.
        queueNotificationForRead(notification);
      }, 2000);

      // Store the timer so it can be cancelled if visibility changes.
      visibilityTimers.set(notificationIdKey, timer);
    };

    // Creates an observer that monitors notification visibility.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Notification ID is stored on the DOM element
          // through the data-notification-id attribute.
          const notificationId = entry.target.dataset.notificationId;

          if (!notificationId) return;

          const notificationIdKey = String(notificationId);

          // Notification must be intersecting and at least 90% visible.
          const isVisible =
            entry.isIntersecting &&
            entry.intersectionRatio >= fullyVisibleThreshold;

          if (isVisible) {
            // Start the 2-second visibility timer.
            startVisibilityTimer(notificationIdKey);
          } else {
            // Cancel the timer when the notification leaves
            // the required visibility threshold.
            clearVisibilityTimer(notificationIdKey);
          }
        });
      },
      {
        // Observe visibility relative to the popup's scroll container.
        root: container,

        // Trigger visibility processing at the 90% threshold.
        threshold: [fullyVisibleThreshold],
      },
    );

    // Select all rendered notification elements that expose
    // a notification ID through the data attribute.
    const notificationElements = container.querySelectorAll(
      "[data-notification-id]",
    );

    // Begin observing every notification element.
    notificationElements.forEach((element) => {
      observer.observe(element);
    });

    // Cleanup the observer and any pending visibility timers
    // when the effect is re-run or the component unmounts.
    return () => {
      observer.disconnect();

      visibilityTimers.forEach((timer) => {
        clearTimeout(timer);
      });

      visibilityTimers.clear();
    };
  }, [isLoading, notificationList, queueNotificationForRead]);

  return (
    <>
      {showSnoozePopup && (
        <div className="fixed z-z-index-max flex items-center justify-center top-top-base left-left-base w-full h-full bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <SnoozePopup
            headerTitle="snooze notification"
            secondHeaderTitle="Select the date and time to snooze this notification."
            btnOneHandleFunction={() => setShowSnoozePopup(false)}
            notificationIdProp={getNotificationId(selectedNotification)}
            onConfirm={(id, snoozed_until) => {
              handleSnoozeConfirm(id, snoozed_until);
            }}
          />
        </div>
      )}
      <div
        className="flex w-full justify-center items-center h-full m-auto top-1/2 left-1/2 fixed z-z-index-modal p-padding-sm"
        style={{ transform: "translate(-50%, -50%)" }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className={`max-h-alert-popup-height-1 xxl:max-h-alert-popup-height-2 tall:max-h-alert-popup-height-2 w-full Qmd:w-small-popup-width flex flex-col pt-padding-md bg-primary-bg rounded-border-radius-md overflow-hidden animate-none ${
            animate
              ? "Qmd:animate-slideInFromLeft"
              : "Qmd:animate-slideInFromRight"
          }`}
        >
          <div className="h-full flex flex-col min-h-height-base flex-1">
            <div className="flex justify-between px-padding-md pb-padding-md">
              <div className="w-full">
                <div className="flex gap-gap-sm">
                  <SVGComponent
                    color="var(--positive-act)"
                    height="var(--icon-md)"
                    width="var(--icon-md)"
                    url={ICONS?.NOTIFICATION_GREY}
                    alt="CustomIcon"
                  />
                  <p className="text-text-primary text-font-md font-semibold">
                    Notifications
                  </p>
                </div>
                {notifyServiceAvailable && shouldShowLatestFiftyText && (
                  <p className="pt-padding-xs text-font-xs text-text-secondary font-font-normal">
                    Showing the latest{" "}
                    {unreadNotificationCount < 50
                      ? renderedNotificationList.length
                      : "50"}{" "}
                    of{" "}
                    {(unreadNotificationCount < 50
                      ? unreadNotificationCount
                      : allNotificationsSummary?.unread
                    )?.toLocaleString()}{" "}
                    unread notifications.
                  </p>
                )}
              </div>
              <div>
                <IconRoundButton
                  enabledBgColor="var(--primary-nav)"
                  handleFunction={closePopup}
                  title="Close"
                  icon={ICONS.CLOSE}
                  hoverOption="white"
                  btnType="button"
                  disabledBgColor="var(--primary-nav)"
                />
              </div>
            </div>
            {isServicesLoading || isLoading ? (
              <div className="flex-1 flex items-center justify-center py-padding-md">
                <Loader
                  width="var(--loader-md)"
                  height="var(--loader-md)"
                />
              </div>
            ) : notifyServiceAvailable ? (
              <div
                ref={scrollContainerRef}
                className="flex-1 min-h-height-base px-padding-md py-padding-sm overflow-y-auto overflow-x-hidden"
              >
                <div
                  className={`flex flex-col gap-gap-md ${
                    !notificationList.length ? "h-full" : ""
                  }`}
                >
                  {!notificationList.length ? (
                    <div className="flex flex-col items-center justify-center h-full gap-gap-md text-center py-padding-sm">
                      <p className="text-font-xs text-text-primary font-normal">
                        There are no unread notifications
                      </p>
                    </div>
                  ) : (
                    renderedNotificationList.map((notification, index) => {
                      const notificationId = getNotificationId(notification);

                      return (
                        <div
                          key={notificationId || index}
                          data-notification-id={notificationId}
                        >
                          <NotificationPopupCard
                            notification={notification}
                            canUseNotifyService={canUseNotifyService}
                            onSnooze={(item) => {
                              setSelectedNotification(item);
                              setShowSnoozePopup(true);
                            }}
                            onFavourite={(item) => {
                              handleFavouriteNotification(item);
                            }}
                            onViewLinkedItem={handleViewLinkedItem}
                            isAnimating={
                              String(animatingNotificationId) ===
                              String(notificationId)
                            }
                            onViewNotification={handleViewNotification}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex justify-center items-center text-center py-padding-lg">
                <p className="text-text-primary text-font-xs font-normal">
                  Service for viewing notifications is not assigned.
                </p>
              </div>
            )}
            <div className="pb-padding-md pt-padding-md px-padding-md flex gap-gap-sm">
              <PrimaryButton
                btnText="Dashboard"
                btnType="button"
                handleFunction={handleDashboardClick}
                isDisabled={
                  !(
                    services?.includes("Admin") ||
                    services?.includes("Notify") ||
                    services?.includes("VChannel") ||
                    services?.includes("CChannel")
                  )
                }
                enabledTextColor="var(--text-primary)"
                enabledBgColor="var(--primary-act)"
                fullWidthRequired={true}
                hoverOption="white"
              />
              <PrimaryButton
                btnText={hasUnreadNotifications ? "View More" : "View all"}
                btnType="button"
                handleFunction={handleViewMoreClick}
                isDisabled={
                  !(
                    services?.includes("Notify") || services?.includes("Admin")
                  ) || !hasAnyNotifications
                }
                enabledTextColor="var(--text-primary)"
                enabledBgColor="var(--primary-act)"
                fullWidthRequired={true}
                hoverOption="white"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationPopup;
